/**
 * Leviathan Web — Permission context
 * Quasar Prime LLC
 *
 * Loads the signed-in user's effective permissions once and shares them.
 *
 * THIS IS CONVENIENCE, NOT SECURITY. Every decision made here is also made by
 * the API, which is what actually refuses. Hiding a navigation item spares
 * someone a 403 they cannot act on; it does not stop anyone who types the URL.
 * If this file and the server ever disagree, the server is right.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

interface PermissionState {
  /** Effective permission keys, e.g. "invoices:view". */
  permissions: Set<string>;
  /** Modules this user may open at all. */
  modules: Set<string>;
  isAdministrator: boolean;
  isLoading: boolean;
  /** True if the permission load failed — see `can()` for why that matters. */
  failed: boolean;
  can: (permission: string) => boolean;
  canAccessModule: (module: string) => boolean;
}

const PermissionContext = createContext<PermissionState | null>(null);

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { accessToken, isAuthenticated } = useAuth();
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [modules, setModules] = useState<Set<string>>(new Set());
  const [isAdministrator, setIsAdministrator] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      setPermissions(new Set());
      setModules(new Set());
      setIsAdministrator(false);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setFailed(false);

    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/users/me/permissions`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) throw new Error(String(response.status));

        const data = await response.json();
        if (cancelled) return;

        setPermissions(new Set<string>(data.permissions ?? []));
        setModules(new Set<string>(data.modules ?? []));
        setIsAdministrator(Boolean(data.isAdministrator));
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, isAuthenticated]);

  const value = useMemo<PermissionState>(() => {
    /**
     * On a failed load, show everything rather than nothing.
     *
     * This looks backwards for a permission check and is deliberate. The API
     * refuses independently, so a permissive UI during an outage costs a 403
     * the user can read. A restrictive one would empty the navigation of a
     * legitimate administrator because one request failed, which looks like the
     * product breaking and has no recovery the user can find.
     */
    const can = (permission: string) =>
      failed || isAdministrator || permissions.has(permission);

    const canAccessModule = (module: string) =>
      failed || isAdministrator || modules.has(module);

    return { permissions, modules, isAdministrator, isLoading, failed, can, canAccessModule };
  }, [permissions, modules, isAdministrator, isLoading, failed]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions(): PermissionState {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used inside a PermissionProvider');
  }
  return context;
}
